<script>
  import { afterUpdate, onMount } from "svelte"
  import notify from "../../../../../../Script/notify"
  import { copyToClipboard } from "../../../../SinglePublish/script"

  /**
   * @type {import("../../../Script/document").bulkDocument}
   */
  export let document
  /**
   * @type {string} text to be shown in the card
   */
  let cardText
  /**
   * @type {string} The hexadecimal color code representing a specific card color.
   */
  let cardColor
  /**
   * @type {string} The hexadecimal color code representing a specific text color.
   */
  let textColor
  /**
   * @type {Boolean} whether to show or hide the copy text
   */
  let popover = false

  onMount(() => {
    changeColorAndText()
  })
  afterUpdate(() => {
    changeColorAndText()
  })

  /**
   * Change the card color and text based on the document's state.
   */
  const changeColorAndText = () => {
    if (document.isDocumentFailed) {
      // Set card color and text for a failed document
      cardColor = "#f8d7da"
      textColor = "#721c24"
    } else {
      if (document.txidRevoked) {
        // Set card color and text when the document is successfully revoked
        cardColor = "#C2FFE9"
        textColor = "#005620"
        return
      } else {
        if (!document.revokationSignature) {
          // Set card text and color when waiting for signature generation
          cardText = "Waiting for Signature generation..."
          cardColor = "#F9E0C2"
          textColor = "#EA9633"
          return
        } else {
          // Set card text and color when revoking document
          cardText = "Revoking Document..."
          cardColor = "#CCEAE8"
          textColor = "#1F51FF"
          return
        }
      }
    }
  }

  /**
   * Copies the document URL to the clipboard and displays a popover notification.
   * Uses a try-catch block to handle errors.
   */
  const copyURL = async () => {
    try {
      // Copy the document URL to the clipboard using the 'copyToClipboard' function
      await copyToClipboard(document.url)

      // Set 'popover' to true to show the popover notification
      popover = true

      // Automatically hide the popover after 2000 milliseconds (2 seconds)
      setTimeout(() => {
        popover = false
      }, 500)
    } catch (error) {
      const info = "PAGE: Bulk Revoke\nEVENT: While copying document URL after revokation"
      // If an error occurs during copying, show a danger notification
      notify.danger("There's an issue in copying text", info)
    }
  }
</script>

<div style="background: {cardColor};color: {textColor}" class="relative flex w-full items-center gap-3 rounded-md px-2 py-3 text-base font-medium">
  {#if !document.isDocumentFailed}
    {#if document.txidRevoked}
      <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#22C55E] text-lg text-white">
        <i class="fa-solid fa-check" />
      </div>

      <div class="w-2/5 text-base font-medium">
        <h1 class="truncate">
          Document of <strong>{document.recName}</strong>
          Revoked
        </h1>
      </div>

      <div class="ml-auto flex gap-6 md:gap-3">
        <a href={document.url} target="_blank" class="flex items-center">
          <span>
            <i class="fa-solid fa-arrow-up-right-from-square" />
          </span>
          <span class="hidden text-sm font-bold md:block">&nbsp;&nbsp;Click&nbsp;URL&nbsp;</span>
        </a>
        <div class="relative">
          <button on:click={copyURL} type="button" class="flex items-center">
            <span>
              <i class="fa-solid fa-copy" />
            </span>
            <span class="hidden text-sm font-bold md:block">&nbsp;&nbsp;Copy&nbsp;URL&nbsp;</span>
          </button>
          <div class="absolute left-[20%] rounded-md bg-black px-3 py-1 {popover ? 'animate-copy' : 'hidden'}">
            <h1 class="text-sm font-medium text-white">Copied</h1>
          </div>
        </div>
      </div>
    {:else}
      <div class="relative flex h-12 w-12 items-center justify-center">
        <div style="border-color: {textColor};color:{textColor}" class="absolute flex h-10 w-10 animate-spin rounded-full border-t-[3px] bg-transparent" />
        <div>
          <i class="fa-solid fa-file" />
        </div>
      </div>

      <h1 class="text-base font-bold">{cardText}</h1>
    {/if}
  {:else}
    <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-500 text-lg text-white">
      <i class="fa-solid fa-triangle-exclamation" />
    </div>

    <div class="flex w-2/5 items-center gap-3 text-base font-medium">
      <h1 class="truncate">
        Document of <strong class="text-[#491217]">{document.recName}</strong>
        failed
      </h1>
      <button data-tooltip={document.errorMsg} tooltip-position="top">
        <i class="fa-solid fa-circle-info fa-lg text-[#491217]" />
      </button>
    </div>
  {/if}
</div>
